import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useActiveTenantId, resolveActiveTenantId } from '../lib/tenant';
import { qualificationExpiration } from '../lib/qualifications';
import type { Initiative } from '../types';
import Modal from './Modal';
import Spinner from './Spinner';

interface PersonChoice {
  personId: string; displayName: string; districtNames?: string[]; roleNames: string[];
  eligible: boolean; qualifications: { name: string; expiresOn: string | null }[];
}
interface Result { tenantId: string; areaId: string; areaName: string; available: boolean; selectedPersonId: string | null; people: PersonChoice[]; canManage: boolean }
const read = httpsCallable<object, Result>(functions, 'getInitiativeSpecializedContacts');
const write = httpsCallable(functions, 'setInitiativeSpecializedContact');

export function SpecializedContactStatus({ eligible, areaName }: { eligible: boolean; areaName: string }) {
  return eligible ? null : <span role="status"> — No longer in {areaName} Area</span>;
}

export function SpecializedPersonContext({ person: p }: { person: PersonChoice }) {
  return <span><strong>{p.displayName}{p.roleNames.length ? ` — ${p.roleNames.join(' · ')}` : ''}</strong>
    <span className="subtitle" style={{ display: 'block' }}>{p.districtNames?.join(' · ') || 'Council'}</span>
    {p.qualifications.map((q, i) => {
      const status = qualificationExpiration(q.expiresOn);
      const [year, month, day] = (q.expiresOn ?? '').split('-').map(Number);
      const date = q.expiresOn ? new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
      return <span key={i} style={{ display: 'block', color: status.tone === 'red' ? 'var(--danger)' : status.tone === 'amber' ? 'var(--warn-fg)' : undefined }}>{q.name}{date && ` — ${status.status === 'expired' ? 'Expired ' : ''}${date}`}</span>;
    })}
  </span>;
}

export default function InitiativeSpecializedContacts({ initiative, canManage }: { initiative: Initiative; canManage: boolean }) {
  const ids = Object.keys(initiative.specializedContacts ?? {});
  if (!ids.length) return null;
  return <section className="iv-section"><div className="iv-section-head"><h3>Specialized contacts</h3></div>
    {ids.map(areaId => <AreaContact key={`${initiative.tenantId}:${initiative.id}:${areaId}`} initiative={initiative} canManage={canManage} areaId={areaId} />)}
  </section>;
}

function AreaContact({ initiative, canManage, areaId }: { initiative: Initiative; canManage: boolean; areaId: string }) {
  const tenantId = useActiveTenantId();
  const [state, setState] = useState<{ identity: string; result?: Result; error?: string }>({ identity: '' });
  const [editing, setEditing] = useState(false);
  const identity = `${tenantId}|${initiative.id}|${areaId}|${editing}`;
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const stored = initiative.specializedContacts?.[areaId]?.personId;
  useEffect(() => {
    let cancelled = false;
    setState({ identity });
    void read({ tenantId, initiativeId: initiative.id, areaId, candidates: editing }).then(r => {
      if (cancelled) return;
      if (r.data.tenantId !== tenantId || r.data.areaId !== areaId || resolveActiveTenantId() !== tenantId) throw new Error('Tenant changed.');
      setState({ identity, result: r.data });
      setSelected(r.data.selectedPersonId ?? '');
    }).catch(e => { if (!cancelled) setState({ identity, error: e instanceof Error ? e.message : 'Unable to load Area contact.' }); });
    return () => { cancelled = true; };
  }, [identity, tenantId, initiative.id, areaId, stored, editing, revision]);
  const visible = state.identity === identity ? state : undefined;
  const result = visible?.result;
  const current = result?.people.find(p => p.personId === result.selectedPersonId);
  const name = result?.areaName ?? 'Qualification Area';
  const save = async () => {
    if (busy || !result?.canManage || resolveActiveTenantId() !== tenantId) return;
    setBusy(true);
    try {
      await write({ tenantId, initiativeId: initiative.id, areaId, personId: selected || null });
      setEditing(false); setRevision(v => v + 1);
    } catch (e) { setState(s => ({ ...s, error: e instanceof Error ? e.message : 'Unable to save Area contact.' })); }
    finally { setBusy(false); }
  };
  return <>
    <div className="iv-fact">
      <div className="iv-fact-label">{name} Contact</div>
      {visible?.error && <p role="alert">{visible.error}</p>}
      {!result && !visible?.error ? <Spinner label="Loading Area contact…" /> : result ?
        <div className="iv-fact-value">{current?.displayName ?? (result.selectedPersonId ? 'Selected person unavailable' : 'Not assigned')}</div> : <p>Stored requirement retained; contact could not be resolved.</p>}
      {result && !result.available && <p role="alert">Area unavailable; stored assignment retained.</p>}
      {current && <div className="subtitle">{current.districtNames?.join(' · ') || 'Council'}<SpecializedContactStatus eligible={current.eligible} areaName={name} /></div>}
      {canManage && result?.canManage && <button className="linkbtn" onClick={() => setEditing(true)}>Manage {name} Contact</button>}
    </div>
    {editing && <Modal title={`${name} Contact`} onClose={() => { if (!busy) setEditing(false); }}>
      {visible?.error && <p role="alert">{visible.error}</p>}
      {!result ? <Spinner label="Loading eligible people…" /> : <>
        <p className="subtitle">Choose a Person participating in your council’s {name} Area. Qualifications inform selection; they do not grant Compass permissions.</p>
        <label>{name} Contact
          <select value={selected} onChange={e => setSelected(e.target.value)} disabled={busy}>
            <option value="">Not assigned</option>
            {selected && !result.people.some(p => p.personId === selected && p.eligible) && <option value={selected} disabled>Current contact is no longer eligible — choose a replacement or clear</option>}
            {result.people.filter(p => p.eligible).map(p => <option key={p.personId} value={p.personId}>{p.displayName}{p.roleNames.length ? ` — ${p.roleNames.join(' · ')}` : ''} ({p.districtNames?.join(' · ') || 'Council'})</option>)}
          </select>
        </label>
        {result.people.filter(p => p.eligible && p.personId === selected).map(p => <div key={p.personId} className="set-row"><SpecializedPersonContext person={p} /></div>)}
        {!result.people.length && <p>No eligible people are assigned to this Area.</p>}
      </>}
      <div className="actions"><button className="btn" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
        <button className="btn primary" disabled={busy || !result?.canManage || (!!selected && !result?.people.some(p => p.personId === selected && p.eligible))} onClick={() => void save()}>Save</button></div>
    </Modal>}
  </>;
}
